import type { RefObject } from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

/**
 * Scrolling of the conversation (spec §2 and §3): with Follow latest on and
 * the view within 80 px of the bottom, growing content keeps it at the
 * bottom. Otherwise nothing scrolls by itself: "Jump to latest" appears, and
 * "New content below" when something was added underneath. The reading
 * position is kept per Q&A (null: at the bottom).
 */

export const FOLLOW_THRESHOLD_PX = 80;

const positions = new Map<string, number | null>();

export const isNearBottom = (element: {
  scrollHeight: number;
  scrollTop: number;
  clientHeight: number;
}) =>
  element.scrollHeight - element.scrollTop - element.clientHeight <
  FOLLOW_THRESHOLD_PX;

export const useFollowLatest = ({
  box,
  content,
  inquiryId,
  ready,
  followLatest,
}: {
  box: RefObject<HTMLElement>;
  content: RefObject<HTMLElement>;
  inquiryId: string | undefined;
  /** The Q&A's messages are on screen. */
  ready: boolean;
  followLatest: boolean;
}) => {
  const atBottom = useRef(true);
  const lastHeight = useRef(0);
  const [away, setAway] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const followRef = useRef(followLatest);
  followRef.current = followLatest;

  // Restore the Q&A's position once its messages are rendered.
  useLayoutEffect(() => {
    const element = box.current;
    if (!ready || !element || !inquiryId) return;
    const saved = positions.get(inquiryId);
    element.scrollTop = saved == null ? element.scrollHeight : saved;
    atBottom.current = isNearBottom(element);
    lastHeight.current = content.current?.offsetHeight ?? 0;
    setAway(!atBottom.current);
    setHasNew(false);
  }, [box, content, inquiryId, ready]);

  useEffect(() => {
    const element = content.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const scroller = box.current;
      const height = element.offsetHeight;
      const grew = height > lastHeight.current;
      lastHeight.current = height;
      if (!scroller || !grew) return;
      if (atBottom.current && followRef.current) {
        scroller.scrollTop = scroller.scrollHeight;
      } else if (!isNearBottom(scroller)) {
        setHasNew(true);
        setAway(true);
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [box, content]);

  const onScroll = useCallback(() => {
    const element = box.current;
    // While a Q&A loads, the list collapses and the browser clamps the
    // position: that is not the user's reading position.
    if (!element || !inquiryId || !ready) return;
    atBottom.current = isNearBottom(element);
    setAway(!atBottom.current);
    if (atBottom.current) setHasNew(false);
    positions.set(inquiryId, atBottom.current ? null : element.scrollTop);
  }, [box, inquiryId, ready]);

  const jumpToLatest = useCallback(() => {
    const element = box.current;
    if (!element) return;
    element.scrollTop = element.scrollHeight;
    atBottom.current = true;
    setAway(false);
    setHasNew(false);
    if (inquiryId) positions.set(inquiryId, null);
  }, [box, inquiryId]);

  return { away, hasNew, onScroll, jumpToLatest };
};
